"use client";

import { useId } from "react";

// Payment method badges of the footer design. Visa and Klarna bring their card
// and shadow in one graphic that overhangs the badge by the shadow's blur;
// PayPal and Mastercard sit on a separate white card. Positions are the
// design's, relative to the badge.

/** Filter ids must be unique per page and valid in `url(#…)` references. */
function useFilterId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

function CardShadowFilter(props: Readonly<{ id: string }>) {
  return (
    <filter
      colorInterpolationFilters="sRGB"
      filterUnits="userSpaceOnUse"
      height="46.3125"
      id={props.id}
      width="56.7305"
      x="0"
      y="0"
    >
      <feFlood floodOpacity="0" result="BackgroundImageFix" />
      <feColorMatrix
        in="SourceAlpha"
        result="hardAlpha"
        type="matrix"
        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
      />
      <feOffset />
      <feGaussianBlur stdDeviation="5.625" />
      <feComposite in2="hardAlpha" operator="out" />
      <feColorMatrix
        type="matrix"
        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.07 0"
      />
      <feBlend
        in2="BackgroundImageFix"
        mode="normal"
        result="effect1_dropShadow"
      />
      <feBlend
        in="SourceGraphic"
        in2="effect1_dropShadow"
        mode="normal"
        result="shape"
      />
    </filter>
  );
}

export function FooterVisaBadge() {
  const filterId = useFilterId("footer-visa-shadow");

  return (
    <div className="absolute inset-[-47.24%_-32.87%]">
      <svg
        aria-hidden="true"
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 56.7305 46.3125"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter={`url(#${filterId})`}>
          <rect
            fill="white"
            height="23.8125"
            rx="3.7207"
            width="34.2305"
            x="11.25"
            y="11.25"
          />
          <path
            clipRule="evenodd"
            d="M21.7054 27.3812H19.6317L18.0767 21.3627C18.0029 21.0859 17.8462 20.8411 17.6156 20.7258C17.0403 20.4359 16.4064 20.2052 15.7148 20.0888V19.8571H19.0554C19.5164 19.8571 19.8622 20.2052 19.9199 20.6094L20.7267 24.9508L22.7994 19.8571H24.8155L21.7054 27.3812ZM25.9681 27.3812H24.0096L25.6223 19.8571H27.5807L25.9681 27.3812ZM30.1145 21.9415C30.1721 21.5363 30.5179 21.3046 30.9213 21.3046C31.5553 21.2464 32.2458 21.3628 32.8221 21.6516L33.1679 20.0317C32.5916 19.8 31.9577 19.6836 31.3824 19.6836C29.4816 19.6836 28.0984 20.7258 28.0984 22.1722C28.0984 23.2726 29.0781 23.8504 29.7697 24.1985C30.5179 24.5455 30.8061 24.7772 30.7484 25.1243C30.7484 25.6449 30.1721 25.8766 29.5968 25.8766C28.9052 25.8766 28.2137 25.7031 27.5807 25.4132L27.235 27.0342C27.9265 27.3231 28.6747 27.4394 29.3663 27.4394C31.4976 27.4966 32.8221 26.4554 32.8221 24.8926C32.8221 22.9245 30.1145 22.8092 30.1145 21.9415V21.9415ZM39.6762 27.3812L38.1211 19.8571H36.4509C36.1051 19.8571 35.7593 20.0888 35.644 20.4359L32.7645 27.3812H34.7806L35.183 26.2819H37.6601L37.8906 27.3812H39.6762ZM36.739 21.8834L37.3143 24.7191H35.7016L36.739 21.8834Z"
            fill="#172B85"
            fillRule="evenodd"
          />
        </g>
        <defs>
          <CardShadowFilter id={filterId} />
        </defs>
      </svg>
    </div>
  );
}

export function FooterKlarnaBadge() {
  const filterId = useFilterId("footer-klarna-shadow");

  return (
    <div className="absolute inset-[-47.24%_-32.87%]">
      <svg
        aria-hidden="true"
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 56.7305 46.3125"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter={`url(#${filterId})`}>
          <rect
            fill="#FEB4C7"
            height="23.8125"
            rx="3.7207"
            width="34.2305"
            x="11.25"
            y="11.25"
          />
          <path
            d="M24.9824 22.0107C25.4444 22.0107 25.8735 22.1586 26.2285 22.4111V22.1309H27.4843V26.5078H26.2285V26.2285C25.8735 26.481 25.4444 26.6289 24.9824 26.6289C23.7597 26.6287 22.7685 25.5946 22.7685 24.3193C22.7687 23.0442 23.7598 22.0109 24.9824 22.0107ZM38.2372 22.0107C38.6992 22.0108 39.1275 22.1587 39.4824 22.4111V22.1309H40.7392V26.5078H39.4824V26.2285C39.1275 26.4808 38.6992 26.6288 38.2372 26.6289C37.0144 26.6289 36.0224 25.5947 36.0224 24.3193C36.0226 23.0441 37.0145 22.0107 38.2372 22.0107ZM42.0722 24.9551C42.5076 24.9551 42.8612 25.3233 42.8613 25.7773C42.8613 26.2314 42.5076 26.5996 42.0722 26.5996C41.6369 26.5995 41.2841 26.2313 41.2841 25.7773C41.2842 25.3233 41.6369 24.9552 42.0722 24.9551ZM16.5546 20.1797V26.5098H15.1621V20.1797H16.5546ZM20.0322 20.1797C20.0322 21.55 19.5206 22.8249 18.6083 23.7725L20.5322 26.5098H18.8124L16.7206 23.5352L17.2607 23.1133C18.156 22.4138 18.6699 21.3445 18.6699 20.1797H20.0322ZM22.2539 26.5078H20.9384V20.1807H22.2539V26.5078ZM29.4277 22.7012C29.6795 22.3593 30.149 22.1319 30.6591 22.1318V23.4053C30.6541 23.4052 30.6495 23.4043 30.6445 23.4043C30.1474 23.4043 29.4306 23.7755 29.4306 24.4648V26.5078H28.1415V22.1309H29.4277V22.7012ZM33.79 22.0137C34.802 22.0137 35.5789 22.7062 35.579 23.7246V26.5078H34.3242V24.1992C34.3242 23.5609 34.0062 23.2168 33.454 23.2168C32.939 23.2169 32.5117 23.5427 32.5117 24.208V26.5078H31.2451V22.1318H32.496V22.625C32.8135 22.1767 33.2882 22.0137 33.79 22.0137ZM25.0859 23.1943C24.4574 23.1943 23.9475 23.6977 23.9472 24.3193C23.9472 24.9412 24.4572 25.4463 25.0859 25.4463C25.7146 25.4463 26.2246 24.9412 26.2246 24.3193C26.2243 23.6977 25.7144 23.1944 25.0859 23.1943ZM38.3398 23.1943C37.7114 23.1945 37.2024 23.6978 37.2021 24.3193C37.2021 24.9411 37.7112 25.4461 38.3398 25.4463C38.9685 25.4463 39.4785 24.9412 39.4785 24.3193C39.4782 23.6977 38.9684 23.1943 38.3398 23.1943Z"
            fill="#17120F"
          />
        </g>
        <defs>
          <CardShadowFilter id={filterId} />
        </defs>
      </svg>
    </div>
  );
}

export function FooterPaypalBadge() {
  return (
    <>
      <div className="absolute inset-0 rounded-[2.5px] bg-white" />
      <div className="absolute inset-[13.14%_20.29%_14.29%_21.74%]">
        <svg
          aria-hidden="true"
          className="block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 19.8437 17.281"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M2.38126 11.9933H0.837795C0.732174 11.9933 0.642345 12.0701 0.625874 12.1743L0.00161913 16.1322C-0.0107885 16.2104 0.0496939 16.2808 0.128912 16.2808H0.865775C0.971397 16.2808 1.06123 16.204 1.0777 16.0996L1.24607 15.0321C1.26231 14.9275 1.35236 14.8509 1.45776 14.8509H1.94636C2.9631 14.8509 3.54985 14.3588 3.70317 13.3839C3.7722 12.9573 3.70609 12.6222 3.50629 12.3874C3.28697 12.1297 2.89782 11.9933 2.38126 11.9933ZM2.55933 13.4389C2.47494 13.9928 2.05177 13.9928 1.6426 13.9928H1.40968L1.57308 12.9584C1.58278 12.8959 1.63695 12.8499 1.70014 12.8499H1.80689C2.08561 12.8499 2.34857 12.8499 2.48444 13.0087C2.56539 13.1036 2.59024 13.2443 2.55933 13.4389ZM6.99504 13.4212H6.25591C6.19294 13.4212 6.13852 13.4672 6.12881 13.5298L6.09612 13.7365L6.04442 13.6616C5.88442 13.4293 5.52765 13.3517 5.1715 13.3517C4.35467 13.3517 3.65711 13.9703 3.52123 14.8381C3.45063 15.2709 3.551 15.6848 3.79654 15.9735C4.02182 16.2389 4.34412 16.3495 4.72752 16.3495C5.38561 16.3495 5.75063 15.9263 5.75063 15.9263L5.71763 16.1317C5.7052 16.2103 5.76567 16.2807 5.84442 16.2807H6.51023C6.61613 16.2807 6.70543 16.2039 6.72214 16.0994L7.12163 13.5697C7.13426 13.4919 7.074 13.4212 6.99504 13.4212ZM5.96473 14.8597C5.8934 15.282 5.55825 15.5654 5.13088 15.5654C4.91625 15.5654 4.74465 15.4966 4.63457 15.3662C4.52533 15.2367 4.48376 15.0522 4.51854 14.8469C4.58517 14.4282 4.92596 14.1355 5.34686 14.1355C5.55668 14.1355 5.72734 14.2052 5.83972 14.3368C5.9523 14.4697 5.99701 14.6552 5.96473 14.8597ZM10.1887 13.4211H10.9315C11.0356 13.4211 11.0962 13.5377 11.0371 13.623L8.56678 17.1889C8.52678 17.2466 8.46088 17.281 8.39048 17.281H7.64864C7.54419 17.281 7.4832 17.1634 7.54367 17.0779L8.31288 15.9921L7.49479 13.5912C7.46649 13.5077 7.52811 13.4211 7.61689 13.4211H8.34672C8.44155 13.4211 8.52521 13.4833 8.55257 13.5741L8.98674 15.0241L10.0112 13.5152C10.0513 13.4563 10.1179 13.4211 10.1887 13.4211Z"
            fill="#253B80"
            fillRule="evenodd"
          />
          <path
            clipRule="evenodd"
            d="M18.2418 16.1322L18.8753 12.102C18.885 12.0394 18.9392 11.9933 19.0021 11.9931H19.7153C19.794 11.9931 19.8545 12.0637 19.8421 12.1419L19.2174 16.0995C19.2011 16.204 19.1113 16.2807 19.0055 16.2807H18.3686C18.2899 16.2807 18.2294 16.2104 18.2418 16.1322ZM13.3906 11.9933H11.8469C11.7415 11.9933 11.6517 12.0701 11.6352 12.1743L11.0109 16.1322C10.9985 16.2104 11.059 16.2807 11.1378 16.2807H11.9299C12.0035 16.2807 12.0665 16.2271 12.078 16.154L12.2551 15.0321C12.2714 14.9275 12.3615 14.8508 12.4668 14.8508H12.9552C13.9722 14.8508 14.5588 14.3588 14.7122 13.3839C14.7815 12.9573 14.7149 12.6222 14.5153 12.3874C14.296 12.1297 13.9072 11.9933 13.3906 11.9933ZM13.5687 13.4389C13.4845 13.9928 13.0613 13.9928 12.6519 13.9928H12.4192L12.5829 12.9584C12.5926 12.8959 12.6463 12.8499 12.7097 12.8499H12.8164C13.095 12.8499 13.3581 12.8499 13.4939 13.0087C13.575 13.1035 13.5996 13.2443 13.5687 13.4389ZM18.004 13.4212H17.2654C17.2019 13.4212 17.148 13.4672 17.1385 13.5298L17.1058 13.7365L17.0539 13.6616C16.8939 13.4293 16.5373 13.3517 16.1812 13.3517C15.3644 13.3517 14.667 13.9703 14.5311 14.8381C14.4607 15.2709 14.5607 15.6848 14.8062 15.9735C15.0319 16.2389 15.3538 16.3495 15.7372 16.3495C16.3953 16.3495 16.7602 15.9263 16.7602 15.9263L16.7273 16.1317C16.7149 16.2103 16.7754 16.2806 16.8546 16.2806H17.5201C17.6255 16.2806 17.7153 16.2039 17.7318 16.0994L18.1315 13.5697C18.1437 13.4919 18.0833 13.4212 18.004 13.4212ZM16.9738 14.8597C16.9029 15.282 16.5673 15.5654 16.1398 15.5654C15.9256 15.5654 15.7537 15.4966 15.6435 15.3662C15.5343 15.2367 15.4932 15.0522 15.5276 14.8469C15.5945 14.4282 15.9349 14.1355 16.3558 14.1355C16.5657 14.1355 16.7363 14.2052 16.8488 14.3368C16.9618 14.4697 17.0065 14.6552 16.9738 14.8597Z"
            fill="#179BD7"
            fillRule="evenodd"
          />
          <path
            d="M8.09329 10.5774L8.28306 9.37217L7.86038 9.36236H5.84206L7.24471 0.468556C7.24899 0.441704 7.26319 0.416669 7.28387 0.398893C7.30455 0.381118 7.33108 0.371321 7.35865 0.371321H10.7619C11.8917 0.371321 12.6714 0.606428 13.0785 1.07047C13.2693 1.28817 13.3908 1.51565 13.4497 1.76599C13.5113 2.02869 13.5124 2.34252 13.4522 2.7253L13.4478 2.75323V2.99849L13.6387 3.10662C13.7994 3.19188 13.927 3.28948 14.025 3.40123C14.1884 3.58735 14.294 3.82391 14.3385 4.10437C14.3846 4.39281 14.3694 4.73604 14.294 5.12462C14.2068 5.57162 14.0661 5.96092 13.876 6.27948C13.701 6.573 13.4783 6.8164 13.2138 7.00512C12.9613 7.18434 12.6612 7.32043 12.322 7.40753C11.9932 7.49307 11.6185 7.53631 11.2074 7.53631H10.9425C10.7532 7.53631 10.5692 7.60451 10.4248 7.72681C10.2801 7.85162 10.1842 8.02207 10.1548 8.2086L10.1349 8.31711L9.79965 10.4413L9.7844 10.5194C9.78043 10.5441 9.77354 10.5564 9.76341 10.5648C9.75432 10.5723 9.74126 10.5774 9.72852 10.5774H8.09329Z"
            fill="#253B80"
          />
          <path
            d="M13.8194 2.78166C13.8092 2.8466 13.7977 2.913 13.7846 2.98121C13.3357 5.28548 11.8003 6.08151 9.83923 6.08151H8.84078C8.60098 6.08151 8.39889 6.25565 8.3615 6.49221L7.85026 9.73441L7.7055 10.6534C7.68117 10.8087 7.80096 10.9487 7.95762 10.9487H9.72863C9.93835 10.9487 10.1164 10.7964 10.1494 10.5896L10.1669 10.4995L10.5003 8.38356L10.5218 8.26753C10.5543 8.06001 10.7328 7.90763 10.9425 7.90763H11.2074C12.9232 7.90763 14.2664 7.21101 14.659 5.19514C14.8229 4.35303 14.738 3.64989 14.3041 3.15536C14.1728 3.00624 14.0099 2.88252 13.8194 2.78166Z"
            fill="#179BD7"
          />
          <path
            d="M13.3497 2.5946C13.2812 2.57464 13.2104 2.5565 13.1378 2.54018C13.0649 2.52422 12.9901 2.51007 12.9132 2.49773C12.644 2.45419 12.3491 2.43351 12.033 2.43351H9.36559C9.2999 2.43351 9.23755 2.44839 9.18167 2.47524C9.05864 2.53437 8.96725 2.65084 8.94511 2.79343L8.37758 6.38752L8.36129 6.49237C8.39868 6.25581 8.60077 6.08166 8.84057 6.08166H9.83902C11.8001 6.08166 13.3356 5.28528 13.7844 2.98137C13.7978 2.91316 13.809 2.84677 13.8192 2.78182C13.7056 2.72159 13.5826 2.67007 13.4502 2.62617C13.4176 2.61528 13.3839 2.60477 13.3497 2.5946Z"
            fill="#222D65"
          />
          <path
            d="M8.94513 2.79335C8.96728 2.65076 9.05877 2.53429 9.18169 2.47551C9.23799 2.44866 9.30002 2.43379 9.36561 2.43379H12.0331C12.3492 2.43379 12.6441 2.45447 12.9133 2.49801C12.9902 2.51034 13.065 2.5245 13.1379 2.54046C13.2105 2.55679 13.2812 2.57493 13.3498 2.59488C13.3839 2.60504 13.4176 2.61556 13.4506 2.62609C13.583 2.66998 13.7061 2.72187 13.8196 2.78174C13.9532 1.9302 13.8186 1.35041 13.3582 0.825408C12.8506 0.247442 11.9344 0 10.7622 0H7.35888C7.1194 0 6.91522 0.174145 6.87814 0.411069L5.46067 9.3963C5.43268 9.57406 5.56981 9.73448 5.74903 9.73448H7.85017L8.37771 6.38744L8.94513 2.79335Z"
            fill="#253B80"
          />
        </svg>
      </div>
    </>
  );
}

export function FooterAmexBadge() {
  const filterId = useFilterId("footer-amex-shadow");

  return (
    <div className="absolute inset-[-47.24%_-32.87%]">
      <svg
        aria-hidden="true"
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 56.7305 46.3125"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter={`url(#${filterId})`}>
          <rect
            fill="#1F72CD"
            height="23.8125"
            rx="3.7207"
            width="34.2305"
            x="11.25"
            y="11.25"
          />
          <text
            dominantBaseline="central"
            fill="white"
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize="6.5"
            fontWeight="700"
            textAnchor="middle"
            x="28.365"
            y="23.1563"
          >
            AMEX
          </text>
        </g>
        <defs>
          <CardShadowFilter id={filterId} />
        </defs>
      </svg>
    </div>
  );
}

export function FooterMastercardBadge() {
  return (
    <>
      <div className="absolute inset-0 rounded-[2.5px] bg-white" />
      <div className="absolute inset-[18.7%_13.04%_19.51%_14.91%]">
        <svg
          aria-hidden="true"
          className="block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 24.6631 14.7129"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.1836 0C21.3143 0.000223535 24.6631 3.29377 24.6631 7.35645C24.6631 11.4191 21.3143 14.7127 17.1836 14.7129C15.3314 14.7129 13.6374 14.0489 12.3311 12.9521C11.0248 14.0487 9.33149 14.7129 7.47949 14.7129C3.34869 14.7128 4.03518e-05 11.4191 0 7.35645C0 3.29371 3.34866 0.000122924 7.47949 0C9.3313 0 11.0248 0.663462 12.3311 1.75977C13.6374 0.663236 15.3316 0 17.1836 0Z"
            fill="#ED0006"
          />
          <path
            d="M17.183 0C21.3139 0 24.6625 3.29363 24.6625 7.35645C24.6625 11.4192 21.3139 14.7129 17.183 14.7129C15.3312 14.7129 13.6377 14.0495 12.3314 12.9531C13.939 11.6038 14.9593 9.5979 14.9594 7.35645C14.9594 5.11477 13.9393 3.10808 12.3314 1.75879C13.6376 0.662649 15.3314 3.57867e-05 17.183 0Z"
            fill="#F9A000"
          />
          <path
            d="M12.3314 1.75888C13.9393 3.10817 14.9594 5.11487 14.9594 7.35654C14.9593 9.59799 13.939 11.6039 12.3314 12.9532C10.7239 11.6039 9.70352 9.59796 9.7035 7.35654C9.7035 5.11489 10.7236 3.10817 12.3314 1.75888Z"
            fill="#FF5E00"
          />
        </svg>
      </div>
    </>
  );
}
